import { useState, useEffect, useCallback } from 'react';
import { CheckIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function PendingPromoterApplications({
  promoterId,
  alwaysShow = false,
  title = 'Pending User Applications'
}) {
  const [pendingPromoters, setPendingPromoters] = useState([]);

  const fetchPendingPromoters = useCallback(async () => {
    try {
      // If promoterId is provided, fetch only applications for that promoter
      const endpoint = promoterId 
        ? `https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/pendingpromoters/${promoterId}`
        : 'https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/pendingpromoters';
      
      const response = await fetch(endpoint);
      const data = await response.json();
      setPendingPromoters(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching pending promoters:', err);
    }
  }, [promoterId]);

  const handleApprove = async (userId) => {
    try {
      const response = await fetch(
        'https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/approvepromoter',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to approve promoter');
      }

      // Refresh the list
      fetchPendingPromoters();
    } catch (err) {
      console.error('Error approving promoter:', err);
    }
  };

  const handleReject = async (userId) => {
    try {
      const response = await fetch(
        'https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/rejectpromoter',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to reject promoter');
      }

      // Refresh the list
      fetchPendingPromoters();
    } catch (err) {
      console.error('Error rejecting promoter:', err);
    }
  };

  useEffect(() => {
    fetchPendingPromoters();
  }, [fetchPendingPromoters]);

  if (pendingPromoters.length === 0 && !alwaysShow) {
    return null;
  }

  return (
    <div className="bg-white border rounded-lg p-4 shadow-sm mb-8">
      <h2 className="text-xl font-semibold text-gray-900">
        {title}
      </h2>
      <div className="mb-4 text-sm text-gray-800"><p>Only approve users you wish to be able to manage events</p></div>
      {pendingPromoters.length === 0 ? (
        <p className="text-sm text-gray-500">0 pending</p>
      ) : (
      <div className="space-y-4">
        {pendingPromoters.map((application) => (
          <div 
            key={`${application.UserID}-${application.linkId}`}
            className="flex flex-col p-4 border rounded-lg hover:bg-gray-50"
          >
            <div className="mb-4">
            <div className="text-sm text-gray-800">
                {application.name_first} {application.name_last}
              </div>
              <div className="font-bold text-gray-900">{application.userEmail}</div>
              
              <div className="py-2 text-sm italic text-gray-600">
                {application.BusinessName}
              </div>
            </div>
            <div className="flex justify-between border-t pt-4">
              <button
                onClick={() => handleReject(application.UserID)}
                className="flex items-center text-sm space-x-1 px-4 py-0 text-red-600 hover:text-red-800 rounded-full hover:bg-red-100"
                title="Reject"
              >
                <TrashIcon className="h-5 w-5" />
                <span>Reject</span>
              </button>
              <button
                onClick={() => handleApprove(application.UserID)}
                className="flex items-center text-sm space-x-1 px-4 py-0 text-green-600 hover:text-green-800 rounded-full hover:bg-green-100"
                title="Approve"
              >
                <CheckIcon className="h-5 w-5" />
                <span>Approve</span>
              </button>
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
} 